import axios from 'axios';
import EventBus from 'eventing-bus';
import { setSingleAgent } from './index';
import { all, takeEvery, call, put } from 'redux-saga/effects';
import { GTM_TRACK_MODE } from '../config';
import { trackAgentCreated } from '../../utils/trackingHelper';

// Fetch all agents from the server and update the state with the retrieved data
function* getAllAgents() {
  yield put({ type: 'SET_LOADING' });
  const { error, response } = yield call(getCall, '/automation/agent');
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error['response']['data']['message'] });
    return EventBus.publish('error', error['response']['data']['message']);
  }
  else if (response) {
    yield put({ type: 'SET_ALL_AGENTS', payload: response['data']['body'] });
    yield put({ type: 'SET_ERROR', payload: null });
  }
}

// Fetch all template agents from the server and update the state with the retrieved data
function* getAllTemplateAgents() {
  const { error, response } = yield call(getCall, '/automation/agent/template');
  if (error) return EventBus.publish('error', error['response']['data']['message']);
  else if (response) yield put({ type: 'SET_ALL_TEMPLATE_AGENTS', payload: response['data']['body'] });
}

// Fetch a single agent's details by their ID and update the state
function* getAgentById({ payload }) {
  const { error, response } = yield call(getCall, `/automation/agent/get/${payload}`);
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Unknown error occurred');
  if (response) yield put(setSingleAgent(response['data']['body']));
}

// Create a new agent with the provided data and invoke the success callback if successful
function* createAgent({ payload }) {
  const { successCallback, data } = payload;
  const { error, response } = yield call(postCall, { path: `/automation/agent/create`, payload: data });
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to create agent');
  else if (response) {
    successCallback(response['data']['body']);
    EventBus.publish('success', response['data']['message']);
    // Publish agent created event for real-time updates
    EventBus.publish('agent:created', response['data']['body']);

    // Track agent created (GTM + Meta)
    trackAgentCreated(response['data']['body']);
  }
}

// Update an existing agent's details by their ID with the provided data
function* updateAgentById({ payload }) {
  const { id, data, onSuccess, onError } = payload;
  try {
    const { error, response } = yield call(putCall, { path: `/automation/agent/update/${id}`, payload: data });
    if (error) {
      const errorMessage = error['response']?.['data']?.['message'] || 'Update failed';
      EventBus.publish('error', errorMessage);
      if (onError) onError(new Error(errorMessage));
      return;
    }
    
    if (response) {
      yield put({ type: 'GET_SINGLE_AGENT', payload: id });
      yield put({ type: 'UPDATE_AGENT_SUCCESS', payload: response['data']['body'] });
      if (onSuccess) onSuccess(response['data']['body']);
      // Only show success message for successful updates
      EventBus.publish('success', response['data']['message']);
      // Publish agent updated event for real-time updates
      EventBus.publish('agent:updated', response['data']['body']);
    }
  } catch (err) {
    const errorMessage = err.message || 'Unexpected error occurred during update';
    if (onError) onError(new Error(errorMessage));
  }
}

// Delete an agent by their ID and refresh the agent list upon success
function* deleteAgentById({ payload }) {
  const { error, response } = yield call(deleteCall, `/automation/agent/delete/${payload}`);
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Unknown error occurred');
  else if (response) {
    EventBus.publish('success', response['data']['message']);
    // Publish agent deleted event for real-time updates
    EventBus.publish('agent:deleted', payload);
  }
  yield put({ type: 'GET_ALL_AGENTS' });
}

// Clone an agent by their ID
function* cloneAgentById({ payload }) {
  const { error, response } = yield call(getCall, `/automation/agent/clone/${payload}`);
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to clone agent');
  else if (response) {
    EventBus.publish('success', response['data']['message']);
    yield put({ type: 'GET_ALL_AGENTS' });
  }
}

// Fetch connection details for agents and update the state with the retrieved data
function* fetchConnectionDetails({ payload }) {
  const { error, response } = yield call(postCall, { path: '/automation/agent/getConnection', payload: JSON.stringify(payload), headers: { 'Content-Type': 'application/json' }});
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch connection details');
  if (response) yield put({ type: 'SET_CONNECTION_DETAILS', payload: response['data']['body'] });
}

// Update an agent's voice settings by their ID with the provided data
function* updateAgentVoice({ payload }) {
  const { error, response } = yield call(putCall, { path: `/automation/agent/updateVoice/${payload['id']}`, payload });
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to update voice');
  // else if (response) EventBus.publish('success', response['data']['message']);
  yield put({ type: 'GET_SINGLE_AGENT', payload: payload['id'] });
}


// Watcher saga for handling agent-related actions
function* actionWatcher() {
  yield takeEvery('CREATE_AGENT', createAgent);
  yield takeEvery('GET_ALL_AGENTS', getAllAgents);
  yield takeEvery('UPDATE_AGENT', updateAgentById);
  yield takeEvery('DELETE_AGENT', deleteAgentById);
  yield takeEvery('CLONE_AGENT', cloneAgentById);
  yield takeEvery('GET_SINGLE_AGENT', getAgentById);
  yield takeEvery('UPDATE_AGENT_VOICE', updateAgentVoice);
  yield takeEvery('GET_ALL_TEMPLATE_AGENTS', getAllTemplateAgents);
  yield takeEvery('GET_CONNECTION_DETAILS', fetchConnectionDetails);
}

// Root saga for managing all agent-related actions
export default function* rootSaga() { yield all([actionWatcher()]) }

// Helper function to make POST requests to the server
function postCall({ path, payload }) {
  return axios
    .post(path, payload)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make GET requests to the server
function getCall(path) {
  return axios
    .get(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make DELETE requests to the server
function deleteCall(path) {
  return axios
    .delete(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make PUT requests to the server
function putCall({ path, payload, headers = {} }) {
  return axios
    .put(path, payload, {
      headers: {
        'Content-Type': payload instanceof FormData ? 'multipart/form-data' : 'application/json',
        ...headers,
      },
    })
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make PATCH requests to the server
function patchCall({ path, payload }) {
  return axios
    .patch(path, payload)
    .then(response => ({ response }))
    .catch(error => {
      if (error['response']['status'] === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}
